import { AnimatePresence, motion } from 'framer-motion';
import { useEffect, useMemo, useState } from 'react';
import coreLogoMark from '../assets/Logodl.svg';
import iconLogoMark from '../assets/LogoIcon.svg';
import { useReducedMotion } from '../hooks/useReducedMotion';
import { hudBasePath, hudModules, hudPageMeta, hudPathOrder, HudPath, hudPaths, toHudCanonicalUrl, toHudHash } from './content';
import {
  AiAutomationPage,
  CommunicationsPage,
  CookiePolicyPage,
  PrivacyPolicyPage,
  PromotionTermsPage,
  ServiceArchitecturePage,
  TermsOfServicePage,
  VerifiedOperationsPage,
  WorkflowDeliveryPage
} from './pages';
import { CookieConsentBanner, GridBackdrop, TransitionLayer } from './primitives';

const normalizePath = (rawPath: string): string => {
  const stripped = rawPath.replace(/^#/, '').trim();
  if (!stripped || stripped === '/') {
    return hudPaths.home;
  }

  const withLeadingSlash = stripped.startsWith('/') ? stripped : `/${stripped}`;
  const compressed = withLeadingSlash.replace(/\/{2,}/g, '/');
  const withoutTrailingSlash = compressed.endsWith('/') ? compressed.slice(0, -1) : compressed;

  return withoutTrailingSlash || hudPaths.home;
};

const isKnownHudPath = (path: string): path is HudPath => hudPathOrder.some((knownPath) => knownPath === path);

const stripHudBasePath = (pathname: string): string => {
  if (pathname === hudBasePath || pathname === `${hudBasePath}/`) {
    return '/';
  }

  if (pathname.startsWith(`${hudBasePath}/`)) {
    return pathname.slice(hudBasePath.length);
  }

  return pathname;
};

const resolvePathFromLocation = (): HudPath => {
  const hashPath = normalizePath(window.location.hash);
  if (window.location.hash && isKnownHudPath(hashPath)) {
    return hashPath;
  }

  const pathnamePath = normalizePath(stripHudBasePath(window.location.pathname));
  return isKnownHudPath(pathnamePath) ? pathnamePath : hudPaths.home;
};

const statusMetrics = [
  { label: 'Signal', value: 97 },
  { label: 'Stability', value: 94 },
  { label: 'Latency', value: 91 }
] as const;

const polarToPercent = (angleDeg: number, radiusPercent: number) => {
  const radians = (angleDeg * Math.PI) / 180;
  return {
    x: 50 + Math.cos(radians) * radiusPercent,
    y: 50 + Math.sin(radians) * radiusPercent
  };
};

const clamp = (value: number, min: number, max: number): number => Math.max(min, Math.min(value, max));

const getViewport = () => {
  if (typeof window === 'undefined') {
    return { width: 390, height: 844 };
  }

  return { width: window.innerWidth, height: window.innerHeight };
};

let hasPlayedMobileHomeStartup = false;
const mobileHomeStartupStorageKey = 'dl_mobile_home_startup_played';

const hasMobileHomeStartupHistory = (): boolean => {
  if (hasPlayedMobileHomeStartup) {
    return true;
  }

  if (typeof window === 'undefined') {
    return false;
  }

  try {
    return window.sessionStorage.getItem(mobileHomeStartupStorageKey) === '1';
  } catch {
    return false;
  }
};

const markMobileHomeStartupPlayed = () => {
  hasPlayedMobileHomeStartup = true;
  if (typeof window === 'undefined') {
    return;
  }

  try {
    window.sessionStorage.setItem(mobileHomeStartupStorageKey, '1');
  } catch {
    // Ignore storage failures and keep the in-memory marker.
  }
};

const TypedHudText = ({
  text,
  active,
  delay = 0,
  charInterval = 18,
  enabled = true,
  className
}: {
  text: string;
  active: boolean;
  delay?: number;
  charInterval?: number;
  enabled?: boolean;
  className?: string;
}) => {
  const reducedMotion = useReducedMotion();
  const shouldType = enabled && !reducedMotion;
  const [visibleText, setVisibleText] = useState(() => (active && !shouldType ? text : ''));

  useEffect(() => {
    if (!active) {
      setVisibleText(shouldType ? '' : text);
      return;
    }

    if (!shouldType) {
      setVisibleText(text);
      return;
    }

    setVisibleText('');
    let currentIndex = 0;
    let interval = 0;
    const timeout = window.setTimeout(() => {
      interval = window.setInterval(() => {
        currentIndex += 1;
        setVisibleText(text.slice(0, currentIndex));
        if (currentIndex >= text.length) {
          window.clearInterval(interval);
        }
      }, charInterval);
    }, delay * 1000);

    return () => {
      window.clearTimeout(timeout);
      if (interval) {
        window.clearInterval(interval);
      }
    };
  }, [active, charInterval, delay, shouldType, text]);

  const completed = visibleText.length >= text.length;

  return (
    <span className={className}>
      {visibleText}
      {shouldType && active && !completed ? (
        <motion.span
          className="ml-0.5 inline-block h-[0.82em] w-[0.08rem] bg-current align-[-0.16em]"
          animate={{ opacity: [0.15, 1, 0.15] }}
          transition={{ duration: 0.7, repeat: Infinity }}
        />
      ) : null}
    </span>
  );
};

const CountUpPercent = ({
  value,
  active,
  delay = 0,
  duration = 1,
  animate = true,
  className
}: {
  value: number;
  active: boolean;
  delay?: number;
  duration?: number;
  animate?: boolean;
  className?: string;
}) => {
  const reducedMotion = useReducedMotion();
  const shouldAnimate = animate && !reducedMotion;
  const [displayValue, setDisplayValue] = useState(active && !shouldAnimate ? value : 0);

  useEffect(() => {
    if (!active) {
      setDisplayValue(0);
      return;
    }

    if (!shouldAnimate) {
      setDisplayValue(value);
      return;
    }

    let raf = 0;
    let startedAt = 0;
    const timeout = window.setTimeout(() => {
      const tick = (timestamp: number) => {
        if (!startedAt) {
          startedAt = timestamp;
        }

        const progress = Math.min((timestamp - startedAt) / (duration * 1000), 1);
        setDisplayValue(Math.round(value * progress));

        if (progress < 1) {
          raf = window.requestAnimationFrame(tick);
        }
      };

      raf = window.requestAnimationFrame(tick);
    }, delay * 1000);

    return () => {
      window.clearTimeout(timeout);
      if (raf) {
        window.cancelAnimationFrame(raf);
      }
    };
  }, [active, delay, duration, shouldAnimate, value]);

  return <span className={className}>{displayValue}%</span>;
};

type MobileLaunchRoute = {
  path: HudPath;
  label: string;
  shortLabel: string;
  summary: string;
};

const MobileCommandMenu = ({
  open,
  routes,
  reducedMotion,
  onClose
}: {
  open: boolean;
  routes: readonly MobileLaunchRoute[];
  reducedMotion: boolean;
  onClose: () => void;
}) => {
  const [viewport, setViewport] = useState(getViewport);

  useEffect(() => {
    const updateViewport = () => {
      const next = getViewport();
      setViewport((current) => {
        if (current.width === next.width && current.height === next.height) {
          return current;
        }
        return next;
      });
    };

    window.addEventListener('resize', updateViewport, { passive: true });

    return () => {
      window.removeEventListener('resize', updateViewport);
    };
  }, []);

  const menuDiameter = useMemo(() => {
    const byWidth = viewport.width * 0.88;
    const byHeight = viewport.height - 170;
    return clamp(Math.floor(Math.min(byWidth, byHeight, 380)), 256, 380);
  }, [viewport.height, viewport.width]);

  const menuNodeWidth = useMemo(() => clamp(Math.round(menuDiameter * 0.245), 78, 96), [menuDiameter]);
  const centerInset = useMemo(() => clamp(Math.round(menuDiameter * 0.42), 104, 156), [menuDiameter]);
  const orbitRadius = useMemo(() => clamp(38 + Math.round((menuDiameter - 256) / 26), 38, 44), [menuDiameter]);
  const orbitAngles = useMemo(() => {
    const count = Math.max(routes.length, 1);
    const start = -90;
    const step = 360 / count;
    return Array.from({ length: count }, (_, index) => start + index * step);
  }, [routes.length]);

  return (
    <motion.div
      className={`fixed inset-0 z-[58] grid place-items-center bg-[radial-gradient(circle_at_50%_50%,rgba(7,20,25,0.78),rgba(2,6,9,0.92))] px-3 py-6 backdrop-blur-sm ${
        open ? 'pointer-events-auto' : 'pointer-events-none'
      }`}
      initial={false}
      animate={open ? { opacity: 1, scale: 1 } : { opacity: 0, scale: 0.985 }}
      transition={{ duration: 0.26, ease: [0.2, 1, 0.32, 1] }}
      style={{ visibility: open ? 'visible' : 'hidden' }}
      aria-hidden={!open}
      aria-modal={open ? 'true' : undefined}
      role="dialog"
      aria-label="Mobile command menu"
      onClick={onClose}
    >
      <motion.div
        className="pointer-events-none absolute inset-y-0 left-[-30%] w-[22%] bg-[linear-gradient(90deg,transparent,rgba(183,255,106,0.22),transparent)]"
        animate={reducedMotion ? undefined : { x: ['0%', '520%'], opacity: [0, 0.95, 0] }}
        transition={{ duration: 3.6, repeat: Infinity, ease: 'linear' }}
      />

      <div
        className="relative aspect-square"
        style={{ width: `${menuDiameter}px`, height: `${menuDiameter}px` }}
        onClick={(event) => event.stopPropagation()}
      >
        <motion.div
          className="absolute inset-0 rounded-full border border-brand-lime/35"
          animate={reducedMotion ? undefined : { rotate: 360 }}
          transition={{ duration: 20, repeat: Infinity, ease: 'linear' }}
        />
        <motion.div
          className="absolute inset-[7%] rounded-full border border-brand-aqua/35 border-dashed"
          animate={reducedMotion ? undefined : { rotate: -360 }}
          transition={{ duration: 13.5, repeat: Infinity, ease: 'linear' }}
        />
        <motion.div
          className="absolute inset-[18%] rounded-full border border-brand-lime/32 bg-brand-panel/86"
          animate={reducedMotion ? undefined : { opacity: [0.45, 0.92, 0.45] }}
          transition={{ duration: 2.3, repeat: Infinity }}
        />

        {routes.map((route, index) => {
          const point = polarToPercent(orbitAngles[index], orbitRadius);
          return (
            <motion.a
              key={route.path}
              href={toHudHash(route.path)}
              onClick={onClose}
              className="absolute z-20 block -translate-x-1/2 -translate-y-1/2 rounded-md border border-brand-lime/55 bg-brand-panel/92 px-2 py-1.5 text-center backdrop-blur-sm"
              style={{ left: `${point.x}%`, top: `${point.y}%`, width: `${menuNodeWidth}px` }}
              initial={{ opacity: 0, scale: 0.82, y: 10 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.86, y: 8 }}
              transition={
                reducedMotion
                  ? { duration: 0 }
                  : { delay: 0.12 + index * 0.06, duration: 0.28, ease: [0.2, 1, 0.32, 1] }
              }
            >
              <p className="font-mono text-[0.53rem] uppercase tracking-[0.2em] text-brand-lime">NODE {index + 1}</p>
              <p className="mt-1 font-mono text-[0.62rem] uppercase tracking-[0.13em] text-brand-text">{route.shortLabel}</p>
            </motion.a>
          );
        })}

        <div
          className="absolute left-1/2 top-1/2 z-10 grid -translate-x-1/2 -translate-y-1/2 place-items-center rounded-full border border-brand-lime/55 bg-brand-panel-2/90 p-4 text-center shadow-[0_0_30px_rgba(183,255,106,0.25)]"
          style={{ width: `${centerInset}px`, height: `${centerInset}px` }}
        >
          <img
            src={coreLogoMark}
            alt="DeveLogic command core"
            className="object-contain"
            style={{ width: `${Math.round(centerInset * 0.66)}px`, height: `${Math.round(centerInset * 0.66)}px` }}
          />
          <p className="hud-micro mt-0 text-brand-lime -translate-y-3">COMMAND SELECTOR</p>
        </div>
        <button
          type="button"
          onClick={onClose}
          className="absolute left-1/2 top-[calc(100%+0.65rem)] z-20 -translate-x-1/2 rounded-md border border-brand-aqua/45 bg-brand-aqua/10 px-3 py-1 font-mono text-[0.55rem] uppercase tracking-[0.2em] text-brand-aqua"
        >
          Close
        </button>
      </div>
    </motion.div>
  );
};

const MobileHomePanel = ({
  reducedMotion,
  onOpenMenu,
  startupActive,
  startupEnabled
}: {
  reducedMotion: boolean;
  onOpenMenu: () => void;
  startupActive: boolean;
  startupEnabled: boolean;
}) => {
  const phaseTransition = (delay: number, duration = 0.56) =>
    reducedMotion ? { duration: 0 } : { delay, duration, ease: [0.2, 1, 0.32, 1] as [number, number, number, number] };

  return (
    <section className="space-y-4">
      <motion.div
        className="relative overflow-hidden rounded-xl border border-brand-lime/28 bg-brand-panel/86 p-4"
        initial={false}
        animate={startupActive ? { opacity: 1, y: 0 } : { opacity: 0, y: 12 }}
        transition={phaseTransition(0.08, 0.62)}
      >
        <motion.div
          className="pointer-events-none absolute inset-y-0 left-[-32%] w-[28%] bg-[linear-gradient(90deg,transparent,rgba(183,255,106,0.24),transparent)]"
          animate={reducedMotion ? undefined : { x: ['-4%', '520%'], opacity: [0.08, 0.46, 0.1] }}
          transition={{ duration: 4.2, repeat: Infinity, ease: 'linear' }}
        />

        <div className="relative z-10 flex items-center justify-between gap-3">
          <div>
            <p className="hud-micro text-brand-lime">DL-OS // MOBILE ROOT SCREEN</p>
            <TypedHudText
              text="Select a subsystem to launch a focused command flow."
              active={startupActive}
              enabled={startupEnabled}
              delay={0.7}
              charInterval={18}
              className="mt-1 block text-sm text-brand-text-2"
            />
          </div>
          <div className="grid h-12 w-12 place-items-center rounded-md border border-brand-lime/40 bg-brand-panel-2/90">
            <img src={iconLogoMark} alt="DeveLogic Digital logo" className="h-9 w-9 object-contain" />
          </div>
        </div>
      </motion.div>

      <motion.div
        className="relative mx-auto aspect-square w-[min(78vw,20rem,56dvh)]"
        initial={false}
        animate={startupActive ? { opacity: 1, scale: 1 } : { opacity: 0, scale: 0.9 }}
        transition={phaseTransition(0.44, 0.82)}
      >
        <motion.div
          className="absolute inset-0 rounded-full border border-brand-lime/35"
          animate={reducedMotion ? undefined : { rotate: 360 }}
          transition={{ duration: 22, repeat: Infinity, ease: 'linear' }}
        />
        <motion.div
          className="absolute inset-[8%] rounded-full border border-brand-aqua/28 border-dashed"
          animate={reducedMotion ? undefined : { rotate: -360 }}
          transition={{ duration: 14, repeat: Infinity, ease: 'linear' }}
        />
        <motion.div
          className="absolute inset-[18%] rounded-full border border-brand-lime/30 bg-brand-panel/78"
          animate={reducedMotion ? undefined : { opacity: [0.42, 0.86, 0.42] }}
          transition={{ duration: 2.4, repeat: Infinity }}
        />
        <motion.div
          className="pointer-events-none absolute inset-[23%] rounded-full border border-brand-aqua/45"
          animate={reducedMotion ? undefined : { opacity: [0, 0.8, 0], scale: [0.9, 1.18, 1.24] }}
          transition={{ duration: 1.6, repeat: Infinity, ease: 'easeOut' }}
        />
        <motion.div
          className="pointer-events-none absolute left-1/2 top-[11%] z-10 -translate-x-1/2 rounded-md border border-brand-aqua/40 bg-brand-panel-2/90 px-2 py-1"
          animate={reducedMotion ? undefined : { y: [0, -4, 0], opacity: [0.7, 1, 0.7] }}
          transition={{ duration: 1.5, repeat: Infinity }}
        >
          <TypedHudText
            text="Tap Core For Menu"
            active={startupActive}
            enabled={startupEnabled}
            delay={1.18}
            charInterval={16}
            className="font-mono text-[0.56rem] uppercase tracking-[0.2em] text-brand-aqua"
          />
        </motion.div>
        <motion.div
          className="pointer-events-none absolute left-1/2 top-[18%] z-10 -translate-x-1/2 text-brand-aqua"
          animate={reducedMotion ? undefined : { y: [0, 4, 0], opacity: [0.5, 1, 0.5] }}
          transition={{ duration: 1.5, repeat: Infinity }}
        >
          <span className="font-mono text-xs">v</span>
        </motion.div>
        <motion.button
          type="button"
          onClick={startupActive ? onOpenMenu : undefined}
          className="absolute inset-[28%] grid place-items-center rounded-full border border-brand-lime/55 bg-brand-panel-2/90 shadow-[0_0_28px_rgba(183,255,106,0.24)]"
          whileTap={reducedMotion ? undefined : { scale: 0.96 }}
          whileHover={reducedMotion ? undefined : { scale: 1.03 }}
          animate={
            reducedMotion
              ? undefined
              : {
                  boxShadow: ['0 0 18px rgba(183,255,106,0.16)', '0 0 36px rgba(183,255,106,0.34)', '0 0 18px rgba(183,255,106,0.16)']
                }
          }
          transition={{ duration: 2, repeat: Infinity }}
        >
          <img src={coreLogoMark} alt="Open command menu" className="h-[132%] w-[132%] object-contain" />
        </motion.button>
      </motion.div>

      <motion.p
        className="hud-micro -mt-1 text-center text-brand-aqua"
        initial={false}
        animate={startupActive ? { opacity: 1, y: 0 } : { opacity: 0, y: 8 }}
        transition={phaseTransition(1.42, 0.46)}
      >
        <TypedHudText
          text="Central core is interactive"
          active={startupActive}
          enabled={startupEnabled}
          delay={1.46}
          charInterval={16}
          className="hud-micro text-brand-aqua"
        />
      </motion.p>

      <motion.div
        className="rounded-xl border border-brand-aqua/24 bg-brand-panel/82 p-3"
        initial={false}
        animate={startupActive ? { opacity: 1, y: 0 } : { opacity: 0, y: 12 }}
        transition={phaseTransition(1.62, 0.62)}
      >
        <p className="hud-micro text-brand-aqua">SYSTEM TELEMETRY</p>
        <div className="mt-3 space-y-2">
          {statusMetrics.map((metric, index) => (
            <div key={metric.label}>
              <div className="mb-1 flex items-center justify-between">
                <span className="font-mono text-[0.62rem] uppercase tracking-[0.18em] text-brand-text-3">{metric.label}</span>
                <CountUpPercent
                  value={metric.value}
                  active={startupActive}
                  delay={2.04 + index * 0.16}
                  duration={1.15}
                  animate={startupEnabled}
                  className="font-mono text-[0.64rem] text-brand-lime"
                />
              </div>
              <div className="h-[2px] overflow-hidden rounded-full bg-brand-panel-2">
                <motion.div
                  className="h-full bg-gradient-to-r from-brand-lime/85 to-brand-aqua/85"
                  initial={false}
                  animate={{ width: startupActive ? `${metric.value}%` : '0%' }}
                  transition={{ duration: 1.02, delay: 2.02 + index * 0.16, ease: [0.2, 1, 0.32, 1] }}
                />
              </div>
            </div>
          ))}
        </div>
      </motion.div>

    </section>
  );
};

const renderMobileSubsystemPage = (path: HudPath) => {
  switch (path) {
    case hudPaths.serviceArchitecture:
      return <ServiceArchitecturePage />;
    case hudPaths.automationSystems:
      return <AiAutomationPage />;
    case hudPaths.workflowPipeline:
      return <WorkflowDeliveryPage />;
    case hudPaths.verifiedOperations:
      return <VerifiedOperationsPage />;
    case hudPaths.communicationsTerminal:
      return <CommunicationsPage />;
    case hudPaths.termsOfService:
      return <TermsOfServicePage />;
    case hudPaths.privacyPolicy:
      return <PrivacyPolicyPage />;
    case hudPaths.cookiePolicy:
      return <CookiePolicyPage />;
    case hudPaths.promotionTerms:
      return <PromotionTermsPage />;
    default:
      return <ServiceArchitecturePage />;
  }
};

export const HudMobileLandingPage = () => {
  const reducedMotion = useReducedMotion();
  const [commandMenuOpen, setCommandMenuOpen] = useState(false);
  const [startupEnabled, setStartupEnabled] = useState(() => !hasMobileHomeStartupHistory());
  const [homeStartupArmed, setHomeStartupArmed] = useState(() => reducedMotion || hasMobileHomeStartupHistory());
  const [activePath, setActivePath] = useState<HudPath>(() => {
    if (typeof window === 'undefined') {
      return hudPaths.home;
    }
    return resolvePathFromLocation();
  });

  useEffect(() => {
    const syncPathFromLocation = () => {
      const nextPath = resolvePathFromLocation();
      setActivePath(nextPath);
      setCommandMenuOpen(false);

      const canonicalUrl = toHudCanonicalUrl(nextPath);
      const currentUrl = `${window.location.pathname}${window.location.hash}`;
      if (currentUrl !== canonicalUrl) {
        window.history.replaceState(null, '', canonicalUrl);
      }
    };

    syncPathFromLocation();
    window.addEventListener('hashchange', syncPathFromLocation);
    window.addEventListener('popstate', syncPathFromLocation);

    return () => {
      window.removeEventListener('hashchange', syncPathFromLocation);
      window.removeEventListener('popstate', syncPathFromLocation);
    };
  }, []);

  const launchRoutes = useMemo(
    () =>
      hudModules
        .filter((module) => module.path !== hudPaths.home)
        .map((module) => ({
          path: module.path,
          label: module.label,
          shortLabel: module.shortLabel,
          summary: module.summary
        })),
    []
  );

  const homeActive = activePath === hudPaths.home;

  useEffect(() => {
    if (!homeActive) {
      return;
    }

    if (reducedMotion || hasMobileHomeStartupHistory()) {
      setHomeStartupArmed(true);
      setStartupEnabled(false);
      markMobileHomeStartupPlayed();
      return;
    }

    setStartupEnabled(true);
    setHomeStartupArmed(false);
    const armTimeout = window.setTimeout(() => {
      setHomeStartupArmed(true);
    }, 280);

    const commitTimeout = window.setTimeout(() => {
      setStartupEnabled(false);
      markMobileHomeStartupPlayed();
    }, 4600);

    return () => {
      window.clearTimeout(armTimeout);
      window.clearTimeout(commitTimeout);
    };
  }, [homeActive, reducedMotion]);

  return (
    <div className="relative min-h-screen overflow-x-clip bg-brand-bg text-brand-text">
      <GridBackdrop />

      <header className="sticky top-0 z-30 border-b border-brand-lime/20 bg-brand-bg/88 px-3 py-3 backdrop-blur-sm">
        <div className="mx-auto flex w-full max-w-[28rem] items-center justify-between gap-3">
          <div className="flex items-center gap-2">
            <div className="grid h-9 w-9 place-items-center rounded-md border border-brand-lime/35 bg-brand-panel/90">
              <img src={iconLogoMark} alt="DeveLogic logo" className="h-7 w-7 object-contain" />
            </div>
            <div>
              <p className="hud-micro text-brand-lime">DEVELOGIC DIGITAL</p>
              <p className="font-mono text-[0.58rem] uppercase tracking-[0.18em] text-brand-text-3">MOBILE COMMAND MODE</p>
            </div>
          </div>
          <span className="hud-micro rounded border border-brand-aqua/35 bg-brand-panel-2/80 px-2 py-1 text-brand-aqua">ONLINE</span>
        </div>
      </header>

      <main
        className={`relative z-10 ${
          homeActive ? 'mx-auto w-full max-w-[28rem] px-3 pb-28 pt-4' : 'pb-28 pt-14'
        }`}
      >
        <AnimatePresence mode="wait">
          <motion.div
            key={activePath}
            initial={{ opacity: 0, y: 16, filter: 'blur(2px)' }}
            animate={{ opacity: 1, y: 0, filter: 'blur(0px)' }}
            exit={{ opacity: 0, y: -10, filter: 'blur(2px)' }}
            transition={{ duration: 0.32, ease: [0.2, 1, 0.32, 1] }}
          >
            <TransitionLayer label={`${hudPageMeta[activePath].shortLabel} // MOBILE ACTIVATED`} mode={hudPageMeta[activePath].mode} />
            {homeActive ? (
              <MobileHomePanel
                reducedMotion={reducedMotion}
                onOpenMenu={() => setCommandMenuOpen(true)}
                startupActive={homeStartupArmed}
                startupEnabled={startupEnabled}
              />
            ) : (
              renderMobileSubsystemPage(activePath)
            )}
          </motion.div>
        </AnimatePresence>
      </main>

      <MobileCommandMenu
        open={activePath === hudPaths.home && commandMenuOpen}
        routes={launchRoutes}
        reducedMotion={reducedMotion}
        onClose={() => setCommandMenuOpen(false)}
      />
      <CookieConsentBanner />
    </div>
  );
};
